/**
 * App-owned system block: product truth + logged-in identity.
 * Injected before user global instructions so models stop inventing CLIs/nsecs.
 */

import { nip19 } from 'nostr-tools';

import type { WorkspaceSettings } from '@/lib/workspace/types';

export type IdentityDetail = 'minimal' | 'standard' | 'full';

export interface SessionProfile {
  name?: string;
  displayName?: string;
  about?: string;
  nip05?: string;
  lud16?: string;
  website?: string;
  picture?: string;
}

export interface SessionBlockInput {
  loggedIn: boolean;
  pubkey?: string;
  profile?: SessionProfile | null;
  /** NIP-65 relay URLs (for full detail) */
  relayUrls?: string[];
  settings?: Pick<
    WorkspaceSettings,
    'includeNostrIdentity' | 'nostrIdentityDetail' | 'mentionAutoFetchNotes'
  > | null;
}

function shortHex(hex: string, n = 8): string {
  if (hex.length <= n * 2) return hex;
  return `${hex.slice(0, n)}…${hex.slice(-n)}`;
}

function truncate(s: string, max: number): string {
  const t = s.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  return t.slice(0, max - 1).trimEnd() + '…';
}

/**
 * Build the always-on OpenDork session system section.
 */
export function buildSessionBlock(input: SessionBlockInput): string {
  const includeIdentity = input.settings?.includeNostrIdentity !== false;
  const detail: IdentityDetail = input.settings?.nostrIdentityDetail ?? 'standard';
  const autoNotes = input.settings?.mentionAutoFetchNotes !== false;

  const lines: string[] = [
    '# OpenDork session',
    'You are **Dork** inside **OpenDork** — a browser AI workspace (static web app), not a local CLI or server agent.',
    'There is **no** shell, **no** `~/.opendork` config path, and **no** way for you to run terminal commands.',
    '',
    '## Hard rules',
    '- **Never** ask for an `nsec`, seed phrase, bunker password, or private key.',
    '- **Never** invent local file paths, CLI flags, or diagnostic pipelines that require secrets.',
    '- You cannot query Nostr relays yourself mid-turn. Only use identity and Nostr content that appears in this prompt (session block, project context, or “Fetched Nostr content”).',
    '- Prefer npub / NIP-05 / @mentions / attached context. If data is missing, say what you already know and tell the user how to attach more (composer **+ → Nostr**, **@mention**, or paste `note`/`nevent`/`npub`/`naddr`).',
    '',
    '## How Nostr context works in OpenDork',
    '- The app fetches **public** events from the user’s connected relays when:',
    '  1. They paste `note` / `nevent` / `npub` / `nprofile` / `naddr` (or `nostr:`) refs in chat, or',
    '  2. They attach packs via **+ → Nostr** (My profile, My recent notes, People, paste ref), or',
    '  3. They **@mention** someone (resolved to a pubkey; profile' +
      (autoNotes ? ' + recent notes' : '') +
      ' may be injected), or',
    '  4. They invoke **@nostr** (aka @network / @follows) — a keyword search over recent notes from people they follow (not the global firehose), or',
    '  5. Project **Context** includes Nostr items (live packs such as “recent notes” re-fetch at send time when their snapshot is stale).',
    '- OpenDork follows **relay hints** embedded in nevent/naddr/nprofile refs and can hop to an author’s NIP-65 write relays when something is missing from the user’s pool — so long-tail articles and profiles usually resolve.',
    '- Fetched material appears under headings like **Fetched Nostr content**, **Attached Nostr context**, or **Mentioned Nostr people**. Treat it as reference the user wants discussed; cite it (note id, npub, article title) when you use it.',
    '- @mentions that were not resolved to a pubkey are **not** identities — do not guess who they are.',
  ];

  lines.push('', '## Logged-in Nostr identity');

  if (!input.loggedIn || !input.pubkey) {
    lines.push(
      '- status: **not logged in**',
      '- For “my profile / my notes”, ask them to log in (browser extension, bunker, or nostrconnect).',
      '- They can still paste **public** npub/nevent refs about other people.',
    );
  } else if (!includeIdentity) {
    lines.push(
      '- status: logged in (identity **hidden** by user privacy setting)',
      '- Do not claim to know their npub or profile from the app. They can attach **My profile** via + → Nostr or paste refs.',
    );
  } else {
    let npub = '';
    try {
      npub = nip19.npubEncode(input.pubkey);
    } catch {
      npub = input.pubkey;
    }
    const p = input.profile ?? {};
    const display =
      p.displayName?.trim() || p.name?.trim() || '';

    lines.push('- status: **logged in** (browser Nostr session — extension, bunker, or similar)');
    lines.push(`- npub: \`${npub}\``);
    lines.push(`- pubkey: \`${input.pubkey}\` (${shortHex(input.pubkey)})`);

    if (detail === 'standard' || detail === 'full') {
      if (display) lines.push(`- name: ${display}`);
      if (p.name && p.displayName && p.name !== p.displayName) {
        lines.push(`- handle: ${p.name}`);
      }
      if (p.nip05) lines.push(`- nip05: ${p.nip05}`);
      if (p.lud16) lines.push(`- lightning: ${p.lud16}`);
      if (p.website) lines.push(`- website: ${p.website}`);
      if (p.about?.trim()) {
        lines.push(`- about: ${truncate(p.about, 400)}`);
      }
    }

    if (detail === 'full' && input.relayUrls && input.relayUrls.length > 0) {
      const sample = input.relayUrls.slice(0, 8).join(', ');
      const more =
        input.relayUrls.length > 8 ? ` (+${input.relayUrls.length - 8} more)` : '';
      lines.push(`- advertised relays (NIP-65 sample): ${sample}${more}`);
    }

    lines.push(
      '- When the user says **my** profile / notes / history, this identity is the subject. Use the fields above; for post history you need **Fetched Nostr content** from **My recent notes** or @me — if missing, invite them to attach it via **+ → Nostr**.',
    );
  }

  return lines.join('\n');
}
